// Khách quen — logic thuần dùng chung cho api / admin-api / best / web (không đụng DB).
//  · Sổ địa chỉ: khoá chống trùng (addressKey) + quyết định lưu địa chỉ của đơn (planAddressRecord).
//  · Gắn khách vào đơn: SĐT là khoá (resolveCustomerTarget), tìm cả dòng cũ +84 / 84 (phoneLookupVariants).
//  · Tên + SĐT in trên đơn: bản chụp Order.recipientName / recipientPhone, đơn cũ chưa có → theo khách.

import { cleanAdminPrefix, removeVietnameseAccents } from './geo/vietnamAddressResolver.js'

export interface AddressParts {
  street: string
  ward?: string | null
  district?: string | null
  city?: string | null
}

export interface IncomingAddress extends AddressParts {
  lat?: number | null
  lng?: number | null
  geoAddress?: string | null
}

export interface SavedAddressLike<Id> extends IncomingAddress {
  id: Id
  addressKey: string
}

type Part = 'ward' | 'district' | 'city'
const PARTS: readonly Part[] = ['ward', 'district', 'city']

/** Chuẩn hoá một phần địa chỉ để so: NFC → bỏ "Phường / Quận / TP." → bỏ dấu → chỉ giữ chữ + số. */
function partKey(s: string | null | undefined, stripPrefix: boolean): string {
  const nfc = String(s ?? '').normalize('NFC').trim()
  return removeVietnameseAccents(stripPrefix ? cleanAdminPrefix(nfc) : nfc)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** Khoá chống trùng: "Thành phố Hà Nội" / "TP. Hà Nội" / "Hà Nội" như nhau; khác số nhà / phường → khác. */
export function addressKey(a: AddressParts): string {
  return [partKey(a.street, false), ...PARTS.map((p) => partKey(a[p], true))].join('|')
}

const clean = (s: string | null | undefined): string | null => {
  const t = String(s ?? '').trim()
  return t ? t : null
}

export type AddressPlan<Id> =
  | { action: 'skip' }
  | { action: 'touch'; id: Id; patch: Partial<Record<Part, string>> & { addressKey?: string; lat?: number; lng?: number; geoAddress?: string | null } }
  | { action: 'create'; data: { street: string; ward: string | null; district: string | null; city: string | null; lat: number | null; lng: number | null; geoAddress: string | null; addressKey: string } }

/**
 * Lưu địa chỉ của một đơn vào sổ của khách:
 *  · trùng khoá → dùng lại;
 *  · cùng số nhà, các phần còn lại không mâu thuẫn (một bên trống) → gộp, điền phần còn thiếu;
 *  · còn lại → địa chỉ mới. Số nhà < 3 ký tự → bỏ qua.
 * Đơn có toạ độ (ghim bản đồ) → cập nhật toạ độ của địa chỉ dùng lại.
 */
export function planAddressRecord<Id>(saved: readonly SavedAddressLike<Id>[], incoming: IncomingAddress): AddressPlan<Id> {
  const street = clean(incoming.street)
  if (!street || street.length < 3) return { action: 'skip' }
  const parts = { ward: clean(incoming.ward), district: clean(incoming.district), city: clean(incoming.city) }
  const key = addressKey({ street, ...parts })
  const hasGeo = incoming.lat != null && incoming.lng != null
  const geoPatch = hasGeo ? { lat: incoming.lat!, lng: incoming.lng!, geoAddress: clean(incoming.geoAddress) } : {}

  const exact = saved.find((s) => s.addressKey === key)
  if (exact) return { action: 'touch', id: exact.id, patch: { ...geoPatch } }

  const streetKey = partKey(street, false)
  const compatible = saved.find(
    (s) =>
      partKey(s.street, false) === streetKey &&
      PARTS.every((p) => {
        const a = partKey(s[p], true)
        const b = partKey(parts[p], true)
        return !a || !b || a === b
      }),
  )
  if (compatible) {
    const fill: Partial<Record<Part, string>> = {}
    for (const p of PARTS) if (!clean(compatible[p]) && parts[p]) fill[p] = parts[p]!
    const filled = Object.keys(fill).length > 0
    const nextKey = filled ? addressKey({ street: compatible.street, ward: compatible.ward, district: compatible.district, city: compatible.city, ...fill }) : null
    return { action: 'touch', id: compatible.id, patch: { ...fill, ...(nextKey ? { addressKey: nextKey } : {}), ...geoPatch } }
  }

  return {
    action: 'create',
    data: { street, ...parts, lat: hasGeo ? incoming.lat! : null, lng: hasGeo ? incoming.lng! : null, geoAddress: hasGeo ? clean(incoming.geoAddress) : null, addressKey: key },
  }
}

export type AddressBookRow = Extract<AddressPlan<number>, { action: 'create' }>['data'] & { useCount: number; lastUsedAt: Date }

/**
 * Sổ địa chỉ dựng từ các đơn cũ của MỘT khách (đơn cũ → mới), cùng luật chống trùng với lúc tạo
 * đơn (planAddressRecord). Giữ tối đa `max` địa chỉ dùng gần nhất. Dùng cho scripts/backfill-customer-book.ts.
 */
export function buildAddressBook(orders: readonly (IncomingAddress & { at: Date })[], max = 5): AddressBookRow[] {
  const book: (AddressBookRow & { id: number })[] = []
  for (const o of orders) {
    const plan = planAddressRecord(book, o)
    if (plan.action === 'create') book.push({ id: book.length, ...plan.data, useCount: 1, lastUsedAt: o.at })
    else if (plan.action === 'touch') {
      const row = book[plan.id]!
      Object.assign(row, plan.patch)
      row.useCount += 1
      if (o.at > row.lastUsedAt) row.lastUsedAt = o.at
    }
  }
  return book
    .sort((a, b) => b.lastUsedAt.getTime() - a.lastUsedAt.getTime())
    .slice(0, max)
    .map(({ id: _id, ...row }) => row)
}

type When = Date | string | null | undefined
const ts = (d: When) => (d ? new Date(d).getTime() : -Infinity)

/** Địa chỉ dùng mặc định: đã đặt mặc định → dùng gần nhất → tạo gần nhất. */
export function pickDefaultAddress<A extends { isDefault?: boolean | null; lastUsedAt?: When; createdAt?: When }>(addrs: readonly A[]): A | null {
  if (!addrs.length) return null
  const def = addrs.find((a) => a.isDefault)
  if (def) return def
  return [...addrs].sort((a, b) => ts(b.lastUsedAt) - ts(a.lastUsedAt) || ts(b.createdAt) - ts(a.createdAt))[0]!
}

/** "12 Lê Lợi, Bến Nghé, Quận 1, Hồ Chí Minh" */
export function addressLine(a: AddressParts): string {
  return [a.street, a.ward, a.district, a.city].map(clean).filter(Boolean).join(', ')
}

/** SĐT đã chuẩn hoá (0xxxxxxxxx) → các dạng có thể còn lưu ở dòng cũ (+84…, 84…). */
export function phoneLookupVariants(norm: string): string[] {
  if (!norm) return []
  if (!/^0\d{9,10}$/.test(norm)) return [norm]
  return [norm, `+84${norm.slice(1)}`, `84${norm.slice(1)}`]
}

export type CustomerTarget<C> = { kind: 'existing'; customer: C } | { kind: 'attach-phone'; customer: C } | { kind: 'create' }

/**
 * Đơn gắn vào khách nào. SĐT là khoá nghiệp vụ: đã có khách mang SĐT → dùng khách đó (khách được
 * chọn ở form không thắng SĐT). SĐT mới + khách được chọn chưa có SĐT → gắn SĐT vào khách đó.
 * Không SĐT → khách được chọn, hoặc khách mới như trước.
 */
export function resolveCustomerTarget<C extends { phone: string | null }>(p: { phone: string; byPhone: C | null; picked: C | null }): CustomerTarget<C> {
  if (p.phone) {
    if (p.byPhone) return { kind: 'existing', customer: p.byPhone }
    if (p.picked && !p.picked.phone) return { kind: 'attach-phone', customer: p.picked }
    return { kind: 'create' }
  }
  return p.picked ? { kind: 'existing', customer: p.picked } : { kind: 'create' }
}

const PLACEHOLDER_NAMES = new Set(['khach le', 'khach le tai quay'])

/** Tên tạm (POS: "Khách lẻ tại quầy") — trường hợp duy nhất tên khách đã lưu được thay bằng tên trên đơn mới. */
export function isPlaceholderName(name: string | null | undefined): boolean {
  const k = partKey(name, false)
  return !k || PLACEHOLDER_NAMES.has(k)
}

interface RecipientSource {
  recipientName?: string | null
  recipientPhone?: string | null
  customer?: { name: string; phone: string | null } | null
}

/** Tên + SĐT người nhận của đơn: bản chụp lúc tạo đơn; đơn cũ chưa có bản chụp → theo khách. */
export function recipientOf(o: RecipientSource): { name: string; phone: string | null } {
  if (o.recipientName != null) return { name: o.recipientName, phone: o.recipientPhone ?? null }
  return { name: o.customer?.name ?? '', phone: o.customer?.phone ?? null }
}

/** Ghi đè order.customer.name / phone bằng người nhận của đơn — response giữ nguyên dạng cho web / iOS / admin. */
export function withRecipient<T>(order: T): T {
  const o = order as T & RecipientSource
  if (!o || typeof o !== 'object' || !o.customer) return order
  const r = recipientOf(o)
  return { ...o, customer: { ...o.customer, name: r.name, phone: r.phone } }
}
